<template>
  <div>
    <b-form @submit.prevent>
      <b-card class="mb-3">
        <b-row>
          <b-col sm="6">
            <b-form-group label="Session Type:">
              <b-form-input v-model="form.sessionType" readonly></b-form-input>
            </b-form-group>
          </b-col>
          <b-col sm="6">
            <b-form-group label="Player ID:">
              <b-form-input v-model="form.playerId" readonly></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="mt-3">
          <b-col sm="6">
            <b-form-group label="Request ID:">
              <b-form-input v-model="form.requestId" readonly></b-form-input>
            </b-form-group>
          </b-col>
          <b-col sm="6">
            <b-form-group label="Request Date Time:">
              <b-form-input
                v-model="form.requestDateTime"
                readonly
              ></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="mt-3">
          <b-col sm="6">
            <b-form-group label="Start Time:">
              <b-form-input v-model="form.startTime" readonly></b-form-input>
            </b-form-group>
          </b-col>
          <b-col sm="6">
            <b-form-group label="Interval:">
              <b-form-input v-model="form.interval" readonly></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
      </b-card>

      <perimeter-form
        ref="initialPerimeterForm"
        :market-session-id="marketSessionId"
        :market-type="marketType"
        :start-activation="startActivation"
        :available-pods="availablePods"
        :service-id="generatedServiceId"
        :all-selected-pods="emptySet"
        :perimeter-index="0"
        @pod-selected="handlePodSelection"
        @pod-permanently-selected="handlePodPermanentSelection"
        @pod-restored="handlePodRestored"
        @pod-toggled="handlePodToggle"
      />
    </b-form>
  </div>
</template>

<script>
import PerimeterForm from "./PerimeterForm.vue"

export default {
  name: "DsoRequestForm",

  components: {
    PerimeterForm
  },

  props: {
    marketSessionId: {
      type: String,
      required: true
    },
    marketType: {
      type: String,
      required: true
    },
    startActivation: {
      type: String,
      required: true
    },
    availablePods: {
      type: Array,
      required: true
    }
  },

  data () {
    return {
      form: {
        sessionType: "",
        playerId: "IDARETI",
        requestId: "",
        requestDateTime: "",
        startTime: "",
        interval: 0.25,
        marketSession: ""
      },
      generatedServiceId: ""
    }
  },

  created () {
    this.initializeForm()
  },

  methods: {
    initializeForm () {
      this.form.sessionType = this.marketType

      this.form.marketSession = this.marketSessionId

      const now = new Date()
      this.form.requestDateTime = now.toISOString()

      const startTime = new Date(this.startActivation)
      this.form.startTime = startTime.toISOString()

      this.form.requestId = this.generateRequestId(startTime)

      this.generatedServiceId = this.generateServiceId()
    },

    formatMarketType (type) {
      switch (type.toLowerCase()) {
        case "dayahead":
          return "DAY_AHEAD"
        case "realtime":
          return "REAL_TIME"
        default:
          return type.toUpperCase()
      }
    },

    generateRequestId (date) {
      const startDate = new Date(this.form.startTime)
      startDate.setUTCHours(22, 0, 0, 0) // 22:00 UTC = 00:00 Italian time
      const formattedDate = this.formatDate(date)
      return `DSO:::${this.formatMarketType(
        this.marketType
      )}:::${formattedDate}`
    },

    formatDate (date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}T00:00`
    },

    generateServiceId () {
      const date = new Date(this.startActivation)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, "0")
      const day = String(date.getDate()).padStart(2, "0")
      const formattedDate = `${year}-${month}-${day}`
      const marketType = this.formatMarketType(this.marketType).replace(
        "_AHEAD",
        "AHEAD"
      )
      return `${formattedDate}T00:00-${marketType}-M1`
    },

    getFormData () {
      if (!this.$refs.initialPerimeterForm) {
        throw new Error("Perimeter form not found")
      }

      try {
        const perimeterData = this.$refs.initialPerimeterForm.getFormData()

        if (
          !perimeterData.podRequestList ||
          perimeterData.podRequestList.length === 0
        ) {
          throw new Error("At least one POD must be added")
        }

        if (!perimeterData.power || perimeterData.power.length === 0) {
          throw new Error("At least one power entry must be added")
        }

        const enrichedPerimeterData = {
          ...perimeterData,
          isMip: true,
          marketOutcomeId: "idContract",
          reducedQuantity: false,
          podRequestList: perimeterData.podRequestList.map((pod) => ({
            pod: pod.pod,
            baselineId: pod.baselineId,
            productReferenceList: perimeterData.power.map((p) => ({
              index: p.index,
              productCodeP: "defaultContract",
              productCodeQ: "defaultContract"
            }))
          }))
        }

        return {
          ...this.form,
          marketSession: this.marketSessionId,
          flexibility: [enrichedPerimeterData]
        }
      } catch (error) {
        throw new Error(`Error in perimeter form: ${error.message}`)
      }
    },

    resetForm () {
      this.initializeForm()
      if (this.$refs.initialPerimeterForm) {
        this.$refs.initialPerimeterForm.resetForm()
      }
    },

    handlePodSelection (pod) {
      this.$emit("pod-selected", pod)
    },

    handlePodPermanentSelection (podId) {
      this.$emit("pod-permanently-selected", podId)
    },

    handlePodRestored (pod) {
      this.$emit("pod-restored", pod)
    },

    handlePodToggle (data) {
      this.$emit("pod-toggled", data)
    }
  },

  computed: {
    emptySet () {
      return {
        has: () => false,
        add: () => {},
        delete: () => {},
        clear: () => {},
        size: 0
      }
    }
  }
}
</script>

<style scoped>
.table >>> thead {
  background-color: #af0f6a;
  color: white;
}

.table >>> th,
.table >>> td {
  vertical-align: middle;
}
</style>



// WEBPACK FOOTER //
// src/components/DsoRequestForm.vue